import {getFormData, resetFormErrors, showFormErrors} from "../utils/form";
import * as notification from "../components/notifications";
import trans from "../utils/trans";
import {logErrorMessage, showApplicationError} from "../utils/errors";
import {getPriceWithCurrency} from "../utils/helpers";
import {reportEmployerPurchasedProductEvent} from "../utils/analytics";
import $jsAPI from "../front_end_api";

let stripeInstance = null;
let stripeElements = null;
let stripePaymentElement = null;
let stripeReturnUrl = window.stripeReturnUrl;

let employerProduct = window.employerProduct;
let product = window.product;
let currentUser = window.$jBoard.currentUser;
let appLanguage = window.$jBoard.language;
const taxEnabled = window.$jBoard.config['tax_enabled'];
const taxCollectionMode = window.$jBoard.config['tax_mode'];
const taxBehavior = window.$jBoard.config['tax_behavior'];
const addressBasedTaxEnabled = taxEnabled && taxCollectionMode === 'stripe_api';

let updatingCouponInformation = false;

const isStripeConfigured = () => {
  return $jBoard.payments.stripe.account_id !== null;
};

const getStripeClientSecret = () => employerProduct.stripe_client_secret;

const getStripe = () => {
  if (!stripeInstance) {
    stripeInstance = Stripe($jBoard.payments.stripe.key, {
      stripeAccount: $jBoard.payments.stripe.account_id,
    });
  }

  return stripeInstance;
};

const getPaymentForm = () => {
  return $("#form-payment");
};

const getBillingInformation = () => {
  const data = {};

  data.coupon = $('#coupon').val();
  data.additional_invoice_info = $('#v-additional-invoice-info').val();
  data.billing_country = $('#billing-country').val();
  data.billing_zip_code = $('#billing-zip-code').val();

  return data;
};

const updateAddressInformationIfNeeded = () => {
  /**
   * We will live update billing info only if Tax Enabled AND it's based on the address,
   * otherwise there is no need to live update the billing info by making an extra AJAX call.
   */
  if (addressBasedTaxEnabled) {
    updateBillingInformation();
  }
};

/**
 * When returns TRUE it means we everything is ready and we can make the payment call,
 * if the function is called from "payment method", if it returns FALSE, it means
 * we can not continue with the Stripe payment process.
 *
 * @returns {Promise<boolean>}
 */
const updateBillingInformation = async (finalPaymentProcessingRequest = false) => {
  const form = getPaymentForm();
  const billingInformation = !updatingCouponInformation
    ? getBillingInformation()
    : {coupon: $('#coupon').val()};

  showLoadingFormState(finalPaymentProcessingRequest);

  return await axios.patch("/api/employer_products/" + employerProduct.id + "/billing_information", billingInformation)
    .then(response => {
      if (!response.data || !response.data.id) {
        logErrorMessage('Employer Product update: Response without employer product received', {
          status: response.status,
          contentType: response.headers['content-type'],
          data: String(response.data).slice(0, 100),
        });
        showApplicationError();
        removeLoadingFormState(finalPaymentProcessingRequest);

        return false;
      }

      const previousEmployerProductState = employerProduct;

      employerProduct = response.data;

      const previousCouponCode = previousEmployerProductState.coupon_redemption
        ? previousEmployerProductState.coupon_redemption.coupon_code
        : null;

      const currentCouponCode = employerProduct.coupon_redemption
        ? employerProduct.coupon_redemption.coupon_code
        : null;

      /**
       * Show proper notification, when coupon was changed.
       */
      if (currentCouponCode !== previousCouponCode) {
        if (currentCouponCode) {
          notification.success(trans('products.message.coupon.success'));
        } else {
          notification.success(trans('products.message.coupon.removed.success'));
        }
      }

      /**
       * First, check if the product became active as a result of this update.
       * If so, it means a 100% discount was applied, and we need to redirect the user to the success page.
       */
      if (employerProduct.stripe_status === 'active') {
        reportEmployerPurchasedProductEvent(employerProduct);

        setTimeout(() => {
          window.location.replace(getRedirectUrl());
        }, 2000);

        return false;
      }

      resetFormErrors(form);
      removeLoadingFormState(finalPaymentProcessingRequest);
      updateEmployerProductInfo();

      return true;

    })
    .catch(error => {
      if (!error.response || error?.response?.status > 499) {
        logErrorMessage('Employer Product update: Unexpected error response received', error);
      }

      showFormErrors(form, error.response);
      removeLoadingFormState(finalPaymentProcessingRequest);

      return false;
    })
}

const redeemCouponIfNeeded = async () => {
  const currentCouponCode = employerProduct.coupon_redemption
    ? employerProduct.coupon_redemption.coupon_code
    : '';

  /**
   * Automatically call "redeemCoupon" ONLY if the coupon code has changed.
   */
  if (currentCouponCode !== $('.coupon-section input').val().trim()) {
    updatingCouponInformation = true;

    await updateBillingInformation();

    updatingCouponInformation = false;
  }
};

const showLoadingFormState = (finalPaymentProcessingRequest = false) => {
  const form = getPaymentForm();

  // Add disabled state to address fields.
  const billingCountrySelectBox = $('#billing-country');
  const billingZipCodeInput = $('#billing-zip-code');
  billingCountrySelectBox.prop("disabled", true);
  billingCountrySelectBox.selectpicker('refresh');
  billingZipCodeInput.prop("disabled", true);

  // Update states of action buttons.
  const btnRedeem = $('.redeem-btn', form);
  if (updatingCouponInformation) {
    btnRedeem.loading(true);
    $("#total-price").loading(true);
  } else {
    /**
     * We will show loading on total only if updating billing info can update the total and
     * we are not submitting this request for final payment processing.
     */
    if (addressBasedTaxEnabled && !finalPaymentProcessingRequest) {
      $("#total-price").loading(true);
    }

    btnRedeem.attr('disabled', true);
  }

  if (!finalPaymentProcessingRequest) {
    const btnSubmit = $('.btn-submit', form);
    btnSubmit.attr('disabled', true);
  }
};

const removeLoadingFormState = (finalPaymentProcessingRequest) => {
  const form = getPaymentForm();

  $("#total-price").loading(false);

  // Remove disabled state from the address fields.
  const billingCountrySelectBox = $('#billing-country');
  const billingZipCodeInput = $('#billing-zip-code');
  billingCountrySelectBox.prop("disabled", false);
  billingCountrySelectBox.selectpicker('refresh');
  billingZipCodeInput.prop("disabled", false);

  // Update states of action buttons.
  const btnRedeem = $('.redeem-btn', form);
  if (updatingCouponInformation) {
    btnRedeem.loading(false);
  } else {
    btnRedeem.attr('disabled', false);
  }

  if (!finalPaymentProcessingRequest) {
    const btnSubmit = $('.btn-submit', form);
    btnSubmit.attr('disabled', false);
  }
};

// Matches format_tax_rate_for_display() on the PHP side: "5.0" => "5", "9.975" => "9.975".
const formatTaxRateForDisplay = (percentage) => {
  const rate = parseFloat(percentage);

  return isNaN(rate) ? '0' : String(rate);
};

const renderItemizedTaxLines = (taxLines) => {
  const container = $("#purchase-summary-tax-info-itemized");
  container.empty();

  taxLines.forEach((taxLine) => {
    const row = $($("#purchase-summary-tax-line-template").html());

    row.find(".purchase-summary-tax-line-label").text(trans('payments.tax.breakdown', {
      TAX_FULL_TEXT: taxLine.display_name || '',
      TAX_JURISDICTION: taxLine.jurisdiction || '',
      TAX_CODE: (taxLine.tax_type || '').toUpperCase(),
      TAX_PERCENT: formatTaxRateForDisplay(taxLine.percentage) + '%',
    }));

    row.find(".purchase-summary-tax-line-amount").text(getPriceWithCurrency(taxLine.amount || 0));

    container.append(row);
  });
};

const updateEmployerProductInfo = () => {

  const exclusiveTaxCollectionEnabled = taxEnabled && taxBehavior === 'exclusive';

  /**
   * We will show a seperated total info ("#purchase-summary-total-info-block") block only when one of these flags is true.
   *    - Product has trial
   *    - Product has discount
   *    - Job board has tax (exclusive)
   */
  if (window.$productOffersTrialForCurrentUser
    || exclusiveTaxCollectionEnabled
    || employerProduct.coupon_redemption
  ) {
    $("#purchase-summary-total-info-block").show();
  } else {
    $("#purchase-summary-total-info-block").hide();
  }

  // Update discount info
  if (employerProduct.coupon_redemption) {
    const discountedAmount = employerProduct.coupon_discounted_amount;
    $("#purchase-summary-coupon-amount").text(getPriceWithCurrency(-discountedAmount));
    $("#purchase-summary-coupon-info").show();

    $('#coupon-message').text(employerProduct.coupon_redemption.coupon_applied_message).show();
  } else {
    $("#purchase-summary-coupon-info").hide();

    $('#coupon-message').hide();
  }

  // Update tax info
  if (exclusiveTaxCollectionEnabled) {
    if (employerProduct.tax_exempt) {
      $("#purchase-summary-tax-info-itemized").hide();
      $("#purchase-summary-tax-info-single").show();
      $("#purchase-summary-tax-info-select-address-calculate").hide();
      $("#purchase-summary-tax-info-amount").hide();
      $("#purchase-summary-tax-info-exempt").show();
    } else {
      $("#purchase-summary-tax-info-exempt").hide();

      const taxBreakdownLines = taxCollectionMode === 'stripe_api'
        && employerProduct.tax_amount_exclusive !== null
        && Array.isArray(employerProduct.tax_breakdown)
        && employerProduct.tax_breakdown.length
          ? employerProduct.tax_breakdown
          : null;

      if (taxBreakdownLines) {
        renderItemizedTaxLines(taxBreakdownLines);
        $("#purchase-summary-tax-info-single").hide();
        $("#purchase-summary-tax-info-itemized").show();
      } else if (taxCollectionMode === 'fixed_rate') {
        $("#purchase-summary-tax-info-itemized").hide();
        $("#purchase-summary-tax-info-single").show();
        $("#purchase-summary-tax-info-select-address-calculate").hide();
        $("#purchase-summary-tax-info-amount")
          .text(getPriceWithCurrency(employerProduct.tax_amount_exclusive)).show();
      } else if (employerProduct.tax_amount_exclusive === null) {
        $("#purchase-summary-tax-info-itemized").hide();
        $("#purchase-summary-tax-info-single").show();
        $("#purchase-summary-tax-info-select-address-calculate").show();
        $("#purchase-summary-tax-info-amount").hide();
      } else {
        $("#purchase-summary-tax-info-itemized").hide();
        $("#purchase-summary-tax-info-single").show();
        $("#purchase-summary-tax-info-select-address-calculate").hide();
        $("#purchase-summary-tax-info-amount")
          .text(getPriceWithCurrency(employerProduct.tax_amount_exclusive)).show();
      }
    }
  }

  // Update total price
  $("#total-price").text(getPriceWithCurrency(employerProduct.final_price));
};

const initPaymentForm = () => {

  initAddressFields();

  if (isStripeConfigured()) {
    const formsControlStyles = $theme.$styleGroupsCssObjectMap.formsControl;
    const formsControlLabelStyles = $theme.$styleGroupsCssObjectMap.formsControlLabel;

    // Set up Stripe.js and Elements to use in checkout form
    const appearance = {
      variables: {
        colorPrimary: $theme.colors.primary,
        colorBackground: formsControlStyles.backgroundColor,
        colorText: formsControlLabelStyles.color,
        colorTextSecondary: formsControlLabelStyles.color,
        colorDanger: $theme.colors.danger,
        spacingUnit: '4px',
        borderRadius: formsControlStyles.borderBottomLeftRadius,
        spacingGridRow: '16px',
      },
      rules: {
        '.Tab': {
          border: '1px solid ' + formsControlStyles.borderBottomColor,
        },
        '.Input': {
          boxShadow: 'none',
          ...formsControlStyles
        },
        '.Input--invalid': {
          boxShadow: 'none',
        },
        '.Label': {
          ...formsControlLabelStyles
        },
        '.Label--invalid': {
          color: $theme.colors.danger,
        },
        '.Input::placeholder': {
          color: $theme.input.placeholderColor,
        },
        '.Input:focus': {
          ...formsControlStyles,
          outline: '1px solid var(--colorPrimary)',
          boxShadow: 'none',
        },
      },
    };

    stripeElements = getStripe().elements({
      clientSecret: getStripeClientSecret(),
      locale: appLanguage.supported_by_stripe ? appLanguage.code : 'auto',
      appearance: appearance,
    });

    stripePaymentElement = stripeElements.create("payment", {
      terms: {
        auBecsDebit: 'never',
        bancontact: 'never',
        card: 'never',
        ideal: 'never',
        sepaDebit: 'never',
        sofort: 'never',
        usBankAccount: 'never'
      },
      fields: {
        billingDetails: {
          address: 'never',
        }
      }
    }).mount("#payment-element");
  }

  if ($('.coupon-section').length) {
    initCouponRedeem();
  }

  getPaymentForm().submit(() => {
    if (product.type === 'one_time') {
      buyProduct();
    } else {
      subscribeToProduct();
    }

    return false;
  });
};

const initCouponRedeem = () => {
  $('.show-coupon-section').click(function(){
    $('.show-coupon-section').removeClass('text-underlined');
    $('.coupon-section').show('slide');
    $('.coupon-section input').focus();
    return false;
  });

  $('#coupon')
    .on('change', redeemCouponIfNeeded)
    .keydown(function (e) {
      if (e.keyCode === 13) {
        e.preventDefault();
        redeemCouponIfNeeded();
        return false;
      }
    });
};

const initAddressFields = () => {
  const billingCountrySelectBox = $('#billing-country');
  const billingZipCodeInput = $('#billing-zip-code');
  const billingZipCodeLabel = $('#billing-zip-code-label');
  const billingCountrySelectBoxContainer = billingCountrySelectBox.closest('.form-group');
  const billingZipCodeContainer = billingZipCodeInput.closest('.form-group');

  billingZipCodeInput.on('change', updateAddressInformationIfNeeded);

  billingCountrySelectBox.change(function () {
    const selectedOption = $(this).find(':selected');
    const zipCodePlaceholder = selectedOption.attr('data-zip-code-placeholder');
    billingZipCodeInput.val('');

    if (zipCodePlaceholder) {
      billingCountrySelectBoxContainer.removeClass('col-lg-12').addClass('col-lg-6');
      billingZipCodeContainer.removeClass('d-none');
      const zipCodeLabel = selectedOption.attr('data-zip-code-label');
      billingZipCodeLabel.text(zipCodeLabel);
      billingZipCodeInput.attr('placeholder', zipCodePlaceholder);
    } else {
      billingZipCodeContainer.addClass('d-none');
      billingCountrySelectBoxContainer.removeClass('col-lg-6').addClass('col-lg-12');
    }

    updateAddressInformationIfNeeded();
  })
}

const showPaymentError = (message) => {
  const displayError = $('#card-errors');
  const stripePaymentElement = $('#card-element');

  displayError.text(message);
  displayError.show();
  stripePaymentElement.addClass('is-invalid');
};

const hideCardError = () => {
  const displayError = $('#card-errors');
  const stripePaymentElement = $('#card-element');

  displayError.hide();
  stripePaymentElement.removeClass('is-invalid');
};

const buyProduct = async () => {
  const form = getPaymentForm();

  $('.btn-submit', form).loading();

  try {
    resetFormErrors(form);

    hideCardError();

    await stripeElements.submit();
    let clientSecret = getStripeClientSecret();

    const continuePurchase = await updateBillingInformation(true);
    if (!continuePurchase) {
      $('.btn-submit', form).loading(false);
      return;
    }

    clientSecret = getStripeClientSecret();

    const billingCountryVal = $('#billing-country').val();
    const billingZipCodeVal = $('#billing-zip-code').val();
    const paymentResult = await getStripe().confirmPayment({
      clientSecret: clientSecret,
      elements: stripeElements,
      redirect: "if_required",
      confirmParams:{
        return_url: getStripeReturnUrl(),
        payment_method_data : {
          billing_details: {
            address: {
              line1: null,
              line2: null,
              city: null,
              state: null,
              postal_code: (billingZipCodeVal !== '') ? billingZipCodeVal : null,
              country: (billingCountryVal !== '') ? billingCountryVal : null,
            },
          }
        },
      },
      payment_method: {
        billing_details: {
          name: currentUser.name,
          email: currentUser.email
        },
      }
    });

    if(paymentResult.error) {
      console.log('Payment Error', paymentResult.error);
      showPaymentError(paymentResult.error.message);

      $('.btn-submit', form).loading(false);
    } else {
      if (paymentResult.paymentIntent.status === 'succeeded') {
        const data = getFormData(getPaymentForm());

        employerProduct = await completeProductPurchase(employerProduct.id, data);

        reportEmployerPurchasedProductEvent(employerProduct);

        $jsAPI.fireEvent('products:acquired', employerProduct)

        window.location.replace(getRedirectUrl());
      } else {
        logErrorMessage(
          'Received unexpected payment intent status - ' + paymentResult.paymentIntent.status
        );

        showApplicationError();

        $('.btn-submit', form).loading(false);
      }
    }
  } catch (e) {
    console.log('error', e);

    $('.btn-submit', form).loading(false);

    showFormErrors(form, e.response);
  }
};

const subscribeToProduct = async () => {
  const form = getPaymentForm();

  $('.btn-submit', form).loading();

  try {
    resetFormErrors(form);

    hideCardError();

    const continuePurchase = await updateBillingInformation(true);
    if (!continuePurchase) {
      $('.btn-submit', form).loading(false);
      return;
    }

    await stripeElements.submit();

    let clientSecret = getStripeClientSecret();

    const billingCountryVal = $('#billing-country').val();
    const billingZipCodeVal = $('#billing-zip-code').val();
    const paymentResult = await getStripe().confirmSetup({
      clientSecret: clientSecret,
      elements: stripeElements,
      redirect: "if_required",
      confirmParams:{
        return_url: getStripeReturnUrl(),
        payment_method_data : {
          billing_details: {
            address: {
              line1: null,
              line2: null,
              city: null,
              state: null,
              postal_code: (billingZipCodeVal !== '') ? billingZipCodeVal : null,
              country: (billingCountryVal !== '') ? billingCountryVal : null,
            },
          }
        }
      },
      payment_method: {
        billing_details: {
          name: currentUser.name,
          email: currentUser.email
        },
      }
    });

    if(paymentResult.error) {
      console.log('Payment Error', paymentResult.error);
      showPaymentError(paymentResult.error.message);

      $('.btn-submit', form).loading(false);
    } else {
      if (paymentResult.setupIntent.status === 'succeeded') {
        const data = getFormData(getPaymentForm());
        data.payment_method = paymentResult.setupIntent.payment_method;

        employerProduct = await completeProductPurchase(employerProduct.id, data);

        reportEmployerPurchasedProductEvent(employerProduct);

        $jsAPI.fireEvent('products:acquired', employerProduct)

        window.location.replace(getRedirectUrl(employerProduct));
      } else {
        logErrorMessage(
          'Received unexpected payment intent status - ' + paymentResult.setupIntent.status
        );

        showApplicationError();

        $('.btn-submit', form).loading(false);
      }
    }
  } catch (e) {
    console.log('error', e);

    $('.btn-submit', form).loading(false);

    showFormErrors(form, e.response);
  }
};

const completeProductPurchase = async (id, data) => {
  let response = await axios.post("/api/employer_products/" + id + "/payments", data)

  employerProduct = response.data;

  return employerProduct;
};

const getRedirectUrl = (employerProduct=null) => {
  const form = getPaymentForm();

  if (employerProduct && !['active', 'trialing'].includes(employerProduct.stripe_status)) {
    return form.data('failure-redirect');
  }

  return form.data('success-redirect');
};

const getStripeReturnUrl = () => {
  return stripeReturnUrl;
};

const initBackButton = () => {
  $("#products-action-back-btn, #invalid-product-action-back-btn").click(function(e){
    e.preventDefault();
    window.history.back();
  });
}

const initPage = () => {
  if (getPaymentForm().length > 0) {
    initPaymentForm();
  }

  /**
   * Since we can have the back button without showing the payment form(in case of a global error message),
   * we need to put the init outside of if block.
   */
  initBackButton();
  return;
};

$(initPage);
